// Shares the domain pattern generated from the server's allowed-domain list.
export class NytieRegistrationEmail {
  constructor(field) {
    this.field = field;
    this.input = field.querySelector('input[type="email"]');
    this.error = field.querySelector('span.form-error');
    this.pattern = new RegExp(this.input.dataset.pattern);
    this.error.id = `${this.input.id}-domain-error`;
    this.error.setAttribute("aria-live", "polite");
    this.input.addEventListener("input", () => this.validate());
  }

  validate() {
    // A server error belongs to the previously submitted value. Keep the
    // current browser error slot, which Decidim also uses on blur and submit.
    this.field.querySelectorAll("small.form-error").forEach((error) => error.remove());
    const invalid = !this.pattern.test(this.input.value);
    this.error.textContent = this.input.value ? this.field.dataset.domainMessage : this.field.dataset.emptyMessage;
    this.error.classList.toggle("is-visible", invalid);
    this.input.classList.toggle("is-invalid-input", invalid);
    if (invalid) {
      this.input.setAttribute("aria-invalid", "true");
      this.input.setAttribute("aria-describedby", this.error.id);
    } else {
      this.input.removeAttribute("aria-invalid");
      this.input.removeAttribute("aria-describedby");
    }
    return !invalid;
  }
}

// Capture before the upstream newsletter handler: an invalid form must never
// open the opt-in dialog. Password checks use the server's registration rules.
export class NytieRegistrationValidation {
  constructor(form, { request = (url, options) => fetch(url, options), delay = 300 } = {}) {
    this.form = form;
    this.request = request;
    this.delay = delay;
    this.email = form.querySelector('input[type="email"]');
    this.password = form.querySelector('input[autocomplete="new-password"]');
    this.agreements = [...form.querySelectorAll('.nytie-auth__agreement input[type="checkbox"][required]')];
    this.passwordField = this.password.closest(".user-password");
    // Decidim inserts its eye button before the input asynchronously. Put it
    // after the input so normal Tab / Shift+Tab navigation follows the fields.
    const orderPasswordControls = () => {
      const toggle = this.passwordField.querySelector('.input-group__password button[type="button"]');
      if (!toggle) return false;
      this.password.after(toggle);
      return true;
    };
    if (!orderPasswordControls()) {
      const observer = new form.ownerDocument.defaultView.MutationObserver(() => {
        if (orderPasswordControls()) observer.disconnect();
      });
      observer.observe(this.passwordField, { childList: true, subtree: true });
    }
    this.passwordError = form.ownerDocument.createElement("span");
    this.passwordError.className = "form-error";
    this.passwordError.id = `${this.password.id}-live-error`;
    this.passwordError.setAttribute("aria-live", "polite");
    this.passwordField.append(this.passwordError);
    this.sequence = 0;
    this.checkedValue = null;
    this.passwordValid = false;
    this.observedValue = this.values();
    const observeValues = () => {
      const value = this.values();
      if (value === this.observedValue) return;
      this.observedValue = value;
      clearTimeout(this.timer);
      this.sequence += 1;
      this.checkedValue = null;
      this.abort?.abort();
      this.pending = null;
      this.timer = setTimeout(() => this.validatePassword(), this.delay);
    };
    [this.password, this.email].forEach((input) => {
      input.addEventListener("input", observeValues);
      input.addEventListener("change", observeValues);
    });
    // Stock FormValidator checks required/pattern on change and can declare a
    // weak password valid. Restore the server result after that native check,
    // only when it belongs to the current email/password pair.
    this.password.addEventListener("valid.formvalidator", () => {
      if (this.checkedValue === this.values()) this.showPasswordErrors(this.passwordErrors);
    });
    this.agreements.forEach((input) => input.addEventListener("change", () => this.validateTerms()));
    form.addEventListener("submit", (event) => this.submit(event), { capture: true });
  }

  values() { return JSON.stringify([this.email.value, this.password.value]); }

  showPasswordErrors(errors) {
    this.passwordField.querySelectorAll("small.form-error, .input-group__password .form-error").forEach((node) => { if (node !== this.passwordError) node.remove(); });
    this.passwordErrors = errors;
    this.passwordError.textContent = errors.join(". ");
    this.passwordError.classList.toggle("is-visible", errors.length > 0);
    this.password.classList.toggle("is-invalid-input", errors.length > 0);
    this.password.setAttribute("aria-invalid", String(errors.length > 0));
    this.password.setAttribute("aria-describedby", this.passwordError.id);
  }

  async validatePassword() {
    const value = this.values();
    this.observedValue = value;
    if (this.checkedValue === value) {
      // The upstream blur validator can hide server-only password errors.
      this.showPasswordErrors(this.passwordErrors);
      return this.passwordValid;
    }
    // Submit and the debounced live check can overlap. Reuse the same request
    // instead of aborting the check that the first click is waiting for.
    if (this.pending?.value === value) return this.pending.promise;
    const sequence = ++this.sequence;
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    const promise = this.checkPassword(value, sequence, signal);
    this.pending = { value, promise };
    try {
      return await promise;
    } finally {
      if (this.pending?.promise === promise) this.pending = null;
    }
  }

  async checkPassword(value, sequence, signal) {
    try {
      const response = await this.request(this.form.dataset.nytiePasswordUrl, {
        method: "POST", signal,
        headers: { "Content-Type": "application/json", "Accept": "application/json",
          "X-CSRF-Token": this.form.ownerDocument.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: JSON.stringify({ email: this.email.value, password: this.password.value })
      });
      if (!response.ok) return null;
      const { errors } = await response.json();
      if (sequence !== this.sequence || value !== this.values()) return undefined;
      this.checkedValue = value;
      this.passwordValid = errors.length === 0 && this.password.value.length > 0;
      this.showPasswordErrors(errors);
      return this.passwordValid;
    } catch (error) {
      // Normal submission still runs every authoritative server validator.
      return error.name !== "AbortError" ? null : undefined;
    }
  }

  validateTerms() {
    // Recheck explicit data consent after asynchronous password validation.
    const results = this.agreements.map((input) => {
      const error = input.closest(".nytie-auth__agreement").querySelector(".form-error");
      input.classList.toggle("is-invalid-input", !input.checked);
      input.setAttribute("aria-invalid", String(!input.checked));
      error?.classList.toggle("is-visible", !input.checked);
      return input.checked;
    });
    return results.every(Boolean);
  }

  submit(event) {
    if (this.allowSubmission) { this.allowSubmission = false; return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.submitting) return;
    clearTimeout(this.timer);
    const submitter = event.submitter;
    this.submitting = true;
    this.form.setAttribute("aria-busy", "true");
    this.completeSubmission(submitter).finally(() => {
      this.submitting = false;
      this.form.removeAttribute("aria-busy");
    });
  }

  async completeSubmission(submitter) {
    const emailValidator = this.email.closest("[data-nytie-registration-email]").nytieValidator;
    const emailValid = emailValidator.validate();
    const termsValid = this.validateTerms();
    let passwordValid;
    do {
      passwordValid = await this.validatePassword();
      // An autofill/change can supersede the request awaited by the first
      // click. Validate the latest values instead of silently losing the click.
      clearTimeout(this.timer);
    } while (passwordValid === undefined && this.form.isConnected);
    if (!this.form.isConnected) return;
    if (!emailValid || !termsValid || !emailValidator.validate() || !this.validateTerms() || passwordValid === false) {
      this.form.querySelector('[aria-invalid="true"]')?.focus();
      return;
    }
    if (!this.form.reportValidity()) return;
    if (passwordValid === null) {
      // Skip the opt-in prompt if the preview is unavailable; the server
      // still validates the normal form, instead of claiming it is valid.
      const modal = this.form.ownerDocument.getElementById("sign-up-newsletter-modal");
      if (modal) modal.dataset.continue = "true";
    }
    // A cached preview resolves within the original submit event's microtask
    // checkpoint. Browsers suppress requestSubmit while that event is still
    // firing, so wait for a new task before forwarding to native handlers.
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (!this.form.isConnected) return;
    this.allowSubmission = true;
    this.form.requestSubmit(submitter || undefined);
  }
}
